export enum UserSignupMethods {
	EMAIL = 'email',
	APPLE = 'apple',
	FACEBOOK = 'facebook',
	GOOGLE = 'google'
}

export type UserOAuthMethods = Exclude<UserSignupMethods, UserSignupMethods.EMAIL>

export type EmailValidationProps = {
	email: string
}

export type EmailValidationResult = {
	success: boolean
	errors?: Record<keyof EmailValidationProps, string>
}

export type LoginProps = {
	email: string
	password: string
}

export type LoginResult = {
	success: boolean
	errors?: Record<keyof UserOAuthMethods, string>
	access_token?: string
	token_type?: string
	expires_in?: number
	refresh_token?: string
	created_at?: number;
	message?: string;
	name?: string;
	code?: number;
}

export type PasswordResetResult = {
	success: boolean
	message: string
}

export type SummaryResult = {
	first_name: string;
	last_name: string;
	points: number;
	tier: string;
	tier_data: {
		current_tier: {
			id: string;
			name: string;
			label: string;
			expiration_date: string;
		};
		tiers: {
			id: string;
			name: string;
			label: string;
			description: string;
			metric_thresholds: {
				point: number;
				tier_points: number;
			};
		}[];
		primary_metric_name: string;
		tier_in_progression: string;
	};
	current_tier_data: {
		id: string;
		name: string;
		label: string;
		description: string;
		metric_thresholds: {
			point: number;
			tier_points: number;
		};
	};
	metrics: {
		point: {
			label: string;
			balance: number;
			life_time: number;
			last_12mo: number;
			ytd: number;
			last_30d: number;
			mtd: number;
			today: number;
			redeem_total: number;
		},
		tier_points: {
			label: string;
			balance: number;
			life_time: number;
			last_12mo: number;
			ytd: number;
			last_30d: number;
			mtd: number;
			today: number;
			redeem_total: number;
		}
	}
}

export type TierData = {
	current_tier_name: string,
	earn_x_points: number,
	points_label: string,
	next_tier: string,
	current_balance: number,
	level_pts: number,
}

export type ProfileResult = {
	card_id: string;
	member_id: string;
	favorite_location: string;
	total_points_in_period: number;
	mobile_phone: string;
	birthdate: string;
	receive_sms_offers: boolean;
	receive_mail_offers: boolean;
	first_name: string;
	last_name: string;
	email: string;
	mailing_postal_code: string;
	tier_period_end_date: string;
}

export type SignupProps = {
	email: string
	password: string
	first_name: string
	last_name: string
	[key: string]: string
}

export type ProfileProps = {
	first_name?: string
	last_name?: string
	favorite_location?: string
	birthdate?: string
	mobile_phone?: string
	receive_mail_offers?: boolean
	receive_sms_offers?: boolean
	mailing_postal_code?: string
}

export type ProfileUpdateResult = {
	success: boolean
	message?: string
	name?: string
	code?: number
	data?: { member_attributes: [] }
}

export type SignupResult = {
	success: boolean
	errors?: Record<keyof SignupProps, string>
	member?: Partial<SignupProps>
}

export type SocialSignupProps = {
	access_token: string
	provider: UserOAuthMethods
	mode?: 'sign_up' | 'sign_in'
	member?: Partial<SignupProps>
}

export type PasswordResetProps = {
	email: string
}

export type ChangePasswordProps = {
	password: string
	passwordConfirmation: string
	resetPasswordToken: string
}

export type UserAccountConfirmationResult = {
	success: boolean
	message: string
	name: string
	code: number
}

export type UserAccountConfirmationProps = {
	confirmationToken: string
}

export type FavoriteLocationResult = {
	id: number
	integration_id: string
	label: string
	address: string
	city: string
	state: string
	zip_code: string
	success: boolean
}

export type ChangePasswordProfileProps = {
	current_password: string
	password: string
	password_confirmation: string
}

export type ChangePasswordProfileResult = {
	success: boolean
	message: string
	name: string
	code: number
}

// Just the methods for logged in users
export type LoggedInUserServiceInterface = {
	summary(): Promise<SummaryResult | null>
	profile(): Promise<ProfileResult | null>
	getFavoriteLocationById(id: number): Promise<FavoriteLocationResult | null>
	changePasswordProfile(data: ChangePasswordProfileProps): Promise<ChangePasswordProfileResult>
}

// Just the methods for users not logged in
export type LoggedOutUserServiceInterface = {
	login(data: LoginProps): Promise<LoginResult>
	signup(data: SignupProps): Promise<SignupResult>
	emailValidation(data: EmailValidationProps): Promise<EmailValidationResult>
	passwordReset(data: PasswordResetProps): Promise<PasswordResetResult>
	changePassword(data: ChangePasswordProps): Promise<PasswordResetResult>
	socialLogin(data: SocialSignupProps): Promise<LoginResult>
	confirmationToken(data: UserAccountConfirmationProps): Promise<UserAccountConfirmationResult>
	updateProfile(data: ProfileProps): Promise<ProfileUpdateResult>
}

export type UserServiceInterface = LoggedInUserServiceInterface & LoggedOutUserServiceInterface
